import { createContext, useContext, useState, useEffect } from 'react'
import { loadCaseStudies, loadDashboardStats, loadGeographic } from '../services/corpusData'

const CorpusDataContext = createContext()

/**
 * CorpusDataProvider - Fetches corpus data from GitHub on mount
 *
 * Provides:
 * - caseStudies: Array of case studies in dashboard format
 * - stats: Dashboard statistics (totals, averages)
 * - geographic: Geographic data for maps
 * - loading: Boolean indicating if data is still loading
 * - error: Error message if fetch failed
 * - refetch: Function to reload data from GitHub
 */
export function CorpusDataProvider({ children }) {
  const [caseStudies, setCaseStudies] = useState([])
  const [stats, setStats] = useState(null)
  const [geographic, setGeographic] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchData = async () => {
    setLoading(true)
    setError(null)

    try {
      // Fetch case studies first (other data depends on it)
      const studies = await loadCaseStudies()
      setCaseStudies(studies)

      // Fetch stats and geographic in parallel
      const [statsData, geoData] = await Promise.all([
        loadDashboardStats(),
        loadGeographic()
      ])

      setStats(statsData)
      setGeographic(geoData)
      setLoading(false)
    } catch (err) {
      console.error('Failed to load corpus data:', err)
      setError(err.message || 'Failed to load data from GitHub')
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const refetch = () => {
    fetchData()
  }

  return (
    <CorpusDataContext.Provider value={{
      caseStudies,
      stats,
      geographic,
      loading,
      error,
      refetch
    }}>
      {children}
    </CorpusDataContext.Provider>
  )
}

/**
 * Hook to access corpus data
 * Must be used within a CorpusDataProvider
 */
export function useCorpusData() {
  const context = useContext(CorpusDataContext)
  if (!context) {
    throw new Error('useCorpusData must be used within a CorpusDataProvider')
  }
  return context
}

export default CorpusDataContext
